<script setup lang="ts">
import { computed } from "vue";

import { metric_help } from "@/assets/i18n/en.json";
import FloatingTooltip from "@/components/ui/FloatingTooltip.vue";
import MarkdownWidget from "@/components/ui/MarkdownWidget.vue";
import type { MetricDto } from "@/dto";

const { metric } = defineProps<{ metric: MetricDto }>();

const helpKey = computed(() =>
  metric.name
    .toLowerCase()
    .replace(/_mean$/, "")
    .replace(/_std$/, "")
);
const hasHelp = computed(() => helpKey.value in metric_help);
const help = computed(() => {
  const i18n: { math?: string; description?: string; sklearn_doc_url?: string } | undefined = (
    metric_help as any
  )[helpKey.value];
  return {
    math: i18n?.math ?? "",
    description: i18n?.description ?? "",
    sklearn_doc_url: i18n?.sklearn_doc_url,
  };
});
</script>

<template>
  <FloatingTooltip v-if="hasHelp" class="metric-tooltip" strategy="absolute" persist>
    <i class="icon icon-info-circle-2" />
    <template #tooltipContent>
      <div class="metric-tooltip-content text-base-regular">
        <div class="math-section">
          <div class="title"><i class="icon icon-formula" /> Mathematical formula</div>
          <div class="math">
            <MarkdownWidget :source="help.math" :use-highlight="false" />
          </div>
        </div>
        <div class="text-section">
          <div class="title"><i class="icon icon-info-circle-2" /> Why it matters?</div>
          <div class="description">
            <MarkdownWidget :source="help.description" :use-highlight="false" />
          </div>
        </div>
        <div v-if="help.sklearn_doc_url" class="doc-section">
          <div class="title"><i class="icon icon-book" /> Want to learn more?</div>
          <a
            :href="help.sklearn_doc_url"
            target="_blank"
            rel="noopener noreferrer"
            class="doc-link text-base-medium"
          >
            View on scikit-learn website
            <i class="icon icon-external-link" />
          </a>
        </div>
      </div>
    </template>
  </FloatingTooltip>
</template>

<style scoped>
.metric-tooltip {
  cursor: help;

  .icon {
    color: var(--border-secondary);
    vertical-align: middle;
  }
}

.metric-tooltip-content {
  display: flex;
  width: 620px;
  flex-direction: column;
  padding: var(--numbers-12);
  gap: var(--numbers-24);

  .math-section,
  .text-section,
  .doc-section {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-8);
  }

  .title {
    color: var(--content-brand-2);

    .icon {
      vertical-align: text-top;
    }
  }

  .math {
    display: flex;
    min-height: 48px;
    align-items: center;
    justify-content: center;
    padding: var(--numbers-8);
    border: solid var(--border-tertiary) var(--stroke-width-md);
    border-radius: var(--numbers-6);
    color: var(--content-primary-inverse);
  }

  .description {
    color: var(--content-tertiary);
  }

  .doc-link {
    display: inline-flex;
    align-items: center;
    color: var(--content-primary-inverse);
    font-size: inherit;
    gap: var(--numbers-4);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    .icon {
      flex-shrink: 0;
      font-size: 0.85em;
    }
  }
}
</style>
